/**
 * Sentry initialization for Runna frontend.
 * Only active in production (or when VITE_SENTRY_DSN is set locally).
 */
import * as Sentry from '@sentry/react'

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
// Release TEM que bater com o que o @sentry/vite-plugin usou ao subir os source
// maps (vite.config.ts → `commitSha` = VERCEL_GIT_COMMIT_SHA). Antes o runtime
// usava VITE_APP_VERSION||'dev' e, como a env não está setada em PROD, todos os
// eventos vinham com release 'dev' → não casavam com os source maps → stack
// minificada. `__APP_SHA__` é o mesmo commit SHA injetado no build.
const RELEASE = typeof __APP_SHA__ !== 'undefined' ? __APP_SHA__ : (import.meta.env.VITE_APP_VERSION || 'dev')

/** Localhost: dev server. Não queremos ruído de HMR (ex: "useAuth must be used
 *  within an AuthProvider", que só ocorre durante hot-reload) no projeto Sentry
 *  compartilhado. Pra forçar Sentry local, setar VITE_SENTRY_FORCE=true. */
function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
}

/** Environment derivado do HOSTNAME, não do build mode. Build de DEV e de PROD
 *  ambos rodam em MODE=production no Vercel — sem isto, eventos de
 *  runnaflow-dev e runnaflow.com cairiam todos como "production" e os alertas
 *  não separariam dev de prod. */
function resolveEnvironment(): string {
  if (typeof window === 'undefined') return 'production'
  const h = window.location.hostname
  if (isLocalhost()) return 'local'
  if (h.endsWith('-dev.runnaflow.com')) return 'development'
  if (h === 'runnaflow.com' || h === 'www.runnaflow.com' || h === 'admin.runnaflow.com') return 'production'
  if (h.endsWith('.vercel.app')) return 'preview'
  return import.meta.env.MODE || 'production'
}

const ENV = resolveEnvironment()

export function initSentry(): void {
  if (!DSN) {
    if (typeof window !== 'undefined' && !isLocalhost()) {
      console.warn('[sentry] DSN missing — Sentry disabled')
    }
    return
  }

  // Desabilita em localhost (HMR gera erros transientes que não são bugs de
  // produção). Override explícito via VITE_SENTRY_FORCE.
  if (isLocalhost() && import.meta.env.VITE_SENTRY_FORCE !== 'true') {
    return
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // replayIntegration entra lazy após o init (abaixo) — padrão recomendado
      // pela doc; tira ~25-35 kB gzip do first paint (replay roda a 2%).
    ],
    tracesSampleRate: ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.02,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      'Non-Error promise rejection captured',
      // CSP / adblocker blocks — ruído de browser, não bugs nossos.
      // Vimos: Adobe Typekit, vercel.live, esm.sh (extensões + previews).
      /^Blocked '\w+' from '/,
      // Dynamic import retry: quando deploy invalida chunk velho, user com
      // tab aberta vê fetch fail no próximo lazy import. Sentry capturou 3x
      // antes do filtro. ErrorBoundary já força reload no próximo nav.
      'Failed to fetch dynamically imported module',
      'error loading dynamically imported module',
      'Importing a module script failed',
    ],
    beforeSend(event, hint) {
      // Drop network errors from known third-party issues
      const err = hint.originalException as Error | undefined
      if (err && typeof err.message === 'string') {
        if (err.message.includes('NetworkError when attempting to fetch resource')) return null
      }
      return event
    },
  })

  // A12 fix (2026-04-24) preservado: mask all text + block media nos replays
  // (sem isso, replays capturavam emails, valores e roteiros — PII leak).
  // Pra debug específico, marcar elementos com data-sentry-mask="false".
  // Chunk lazy LOCAL (não o lazyLoadIntegration do CDN — CSP é script-src 'self').
  import('./sentry-replay')
    .then(({ replayIntegration }) => {
      Sentry.addIntegration(replayIntegration({ maskAllText: true, blockAllMedia: true }))
    })
    .catch(() => { /* replay é best-effort — se o chunk falhar, app segue sem replay */ })
}

/** Manual capture helper (used by audit.ts etc) */
export { captureException, captureMessage, setUser, setTag, setContext, addBreadcrumb } from '@sentry/react'
